import { useUpdateDashboard } from "api/api-hooks/dashboards-hooks";
import { Dashboard, TabsDisplay } from "api/model/dashboard/Dashboard";
import { ContextTypes, DashboardTabModel } from "api/model/dashboard/DashboardTab";
import CreateDatasetDialog from "components/dashboard/dataset-window/datasets-menu/create-dataset-dialog/CreateDatasetDialog";
import {
  easasErrorAlert,
  easasSuccessAlert,
} from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import useTabId from "hooks/dashboard/useTabId";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputSwitch, InputSwitchChangeEvent } from "primereact/inputswitch";
import { Menu } from "primereact/menu";
import { MenuItem } from "primereact/menuitem";
import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { I, T } from "services/lang/LanguageService";
import useDashboardStore from "store/dashboard-store";
import useChartDialogStore from "store/chart-dialog-store";
import useChartInDialogStore from "store/chart-in-dialog-store";
import "../dashboard-styles/DashboardMain.scss";
import DashboardEditor from "./DashboardEditor";
import ImportDashboardOrTab, { handleAddNewTab } from "./ImportDashboardOrTab";
import AddNewTabGroup from "./tab/AddNewTabGroup";
import DashboardTabConfigurator from "./tab/DashboardTabConfigurator";
import TabContextMenu from "./tab/TabContextMenu";
import TabTitleEditor from "./tab/TabTitleEditor";
import { ReorderType } from "./tab/reorder/DraggableItem";
import Reorder from "./tab/reorder/Reorder";
import DatasetSearchDialog from "./DatasetSearchDialog";
import ChangeTabsDisplayTypeDialog from "./tab/ChangeTabsDisplayTypeDialog";

export interface ICurrentDashboard {
  dashboard: Dashboard;
  isEditMode: boolean;
  setCurrentDashboard: (dashboard: Dashboard) => void;
}

const tabsOptions = (tabs: DashboardTabModel[]) =>
  tabs.map((tab, index) => ({
    label: I(tab.title),
    value: index,
  }));

const DashboardMain = () => {
  const dashboard = useDashboardStore(state => state.dashboard);
  const isEditMode = useDashboardStore(state => state.isEditMode);
  const setIsEditMode = useDashboardStore(state => state.setIsEditMode);
  const setDashboard = useDashboardStore(state => state.setDashboard);
  const tabId = useTabId();
  const isGroupReorderEnabled = useDashboardStore(state => state.isGroupReorderEnabled);
  const setIsGroupReorderEnabled = useDashboardStore(state => state.setIsGroupReorderEnabled);

  const navigate = useNavigate();
  const { isOpen } = useChartDialogStore();
  const { isDialogVisible } = useChartInDialogStore();

  const [isChangeTabTitleVisible, setIsChangeTabTitleVisible] = useState<boolean>(false);
  const [isImportDashboardDialogVisible, setIsImportDashboardDialogVisibility] =
    useState<boolean>(false);
  const [selectedTabTitle, setSelectedTabTitle] = useState<string>("");
  const [reorderTabsDialogVisibility, setReorderTabsDialogVisibility] = useState<boolean>(false);
  const [addNewGroupDialogVisibility, setAddNewGroupDialogVisibility] = useState<boolean>(false);
  const [tabConfiguratorvisibility, setTabConfiguratorVisibility] = useState<boolean>(false);
  const [isCreateDatasetDialogVisible, setIsCreateDatasetDialogVisible] = useState<boolean>(false);
  const [isDatasetSearchDialogVisible, setIsDatasetSearchDialogVisible] = useState<boolean>(false);
  const [isChangeTabsDisplayTypeDialogVisible, setChangeTabsDisplayTypeDialogVisibility] =
    useState<boolean>(false);

  const updateQuery = useUpdateDashboard({
    onSuccess: () => {
      easasSuccessAlert(T("alert.dashboard-updated"));
    },
    onFailure: (error: Error) => {
      easasErrorAlert(error.message);
    },
  });

  const menuRef = useRef<Menu>(null);

  const onTabChange = useCallback(
    (index: number) => {
      if (index !== +tabId) {
        navigate(`/dashboard/${dashboard?.id}/tab/${index}`);
      }
    },
    [dashboard?.id, navigate, tabId]
  );

  const menuItems = useMemo((): MenuItem[] | undefined => {
    return dashboard?.dashboardTabsJs?.map((tab, index) => ({
      label: tab.title,
      className: tabId === index ? "bg-primary-100" : "",
      command: () => onTabChange(index),
    }));
  }, [dashboard?.dashboardTabsJs, onTabChange, tabId]);

  if (!dashboard || !dashboard.dashboardTabsJs) {
    return <ProgressSpinner />;
  }

  if (dashboard.dashboardTabsJs.length === 0) {
    handleAddNewTab(dashboard, setDashboard, T("word.new-tab"));
  }

  const currentTab = dashboard?.dashboardTabsJs?.[tabId];
  const currentTabContextType = currentTab?.contextConfig?.contextType || ContextTypes.NONE;

  const handleEditMode = (e: InputSwitchChangeEvent) => {
    if (!e.value) {
      setIsGroupReorderEnabled(e.value);
    }
    setIsEditMode(e.value);
  };

  const triggerTabTitleChangeDialog = () => {
    setSelectedTabTitle(dashboard.dashboardTabsJs[tabId].title);
    setIsChangeTabTitleVisible(true);
  };

  const handleTabChange = (e: any) => {
    const selectedIndex = e.value;
    if (e.originalEvent.ctrlKey || e.originalEvent.metaKey) {
      const newTabUrl = `${window.location.pathname}/#/dashboard/${dashboard.id}/tab/${selectedIndex}`;
      window.open(newTabUrl, "_blank");
    } else {
      const url = `/dashboard/${dashboard.id}/tab/${selectedIndex}`;
      navigate(url);
    }
  };

  return (
    <>
      {(isOpen || isDialogVisible) && (
        <div
          className="fixed top-0 left-0 right-0 bottom-0"
          style={{
            backgroundColor: "grey",
            opacity: 0.4,
            width: "100vw",
            height: "100vh",
            zIndex: 2,
            pointerEvents: "none",
          }}
        />
      )}
      <div className="flex flex-row justify-content-between mb-3">
        <div>{I(dashboard?.title)}</div>
        <div className="flex flex-row gap-2">
          <div>{T("word.options")}:</div>
          <InputSwitch checked={isEditMode} onChange={e => handleEditMode(e)} />
        </div>
      </div>
      {isEditMode && (
        <DashboardEditor
          updateQuery={updateQuery}
          dashboard={dashboard}
          setDashboard={setDashboard}
          tabId={tabId}
          triggerTabTitleChangeDialog={triggerTabTitleChangeDialog}
          setReorderTabsDialogVisibility={setReorderTabsDialogVisibility}
          setTabConfiguratorVisibility={setTabConfiguratorVisibility}
          setIsImportDashboardDialogVisibility={setIsImportDashboardDialogVisibility}
          setAddNewGroupDialogVisibility={setAddNewGroupDialogVisibility}
          isGroupReorderEnabled={isGroupReorderEnabled}
          setIsGroupReorderEnabled={setIsGroupReorderEnabled}
          setIsCreateDatasetDialogVisible={setIsCreateDatasetDialogVisible}
          setDatasetSearchDialogVisibility={setIsDatasetSearchDialogVisible}
          setChangeTabsDisplayTypeDialogVisibility={setChangeTabsDisplayTypeDialogVisibility}
        />
      )}
      <div>
        {(dashboard.tabsDisplay ?? TabsDisplay.ROWS) === TabsDisplay.ROWS ? (
          <div className="tabsFlex">
            {dashboard?.dashboardTabsJs?.map((tab, index) => (
              <Link
                key={tab.uuid}
                to={`/dashboard/${dashboard.id}/tab/${index}`}
                className={`tab dashboard-tab ${
                  tabId !== undefined && tabId === index ? "tab-selected" : "tab-unselected"
                }`}
              >
                {I(tab.title)}
              </Link>
            ))}
          </div>
        ) : (
          <div className="flex flex-row gap-5 align-items-end justify-content-between mb-4">
            <Dropdown
              className="w-2 h-3rem"
              value={tabId}
              options={tabsOptions(dashboard.dashboardTabsJs)}
              optionLabel="label"
              optionValue="value"
              filter
              scrollHeight="600px"
              placeholder={I("Select a tab")}
              onChange={handleTabChange}
            />
            {currentTabContextType && currentTabContextType !== ContextTypes.NONE && (
              <TabContextMenu tabId={tabId} />
            )}
          </div>
        )}
        <div className="mobile-menu">
          <Button
            onClick={event => menuRef.current?.toggle(event)}
            className="p-button-icon-only mb-3 w-12"
          >
            <i className="pi pi-bars mr-2 text-2xl" />
            <div className="font-bold text-xl">{T("word.tabs")}</div>
          </Button>
          <Menu ref={menuRef} model={menuItems} popup className="w-12 menu-list" />
        </div>
      </div>
      {currentTabContextType &&
        currentTabContextType !== ContextTypes.NONE &&
        (dashboard.tabsDisplay ?? TabsDisplay.ROWS) === TabsDisplay.ROWS && (
          <div className="flex my-4 justify-content-end">
            <TabContextMenu tabId={tabId} />
          </div>
        )}
      {isChangeTabTitleVisible && (
        <TabTitleEditor
          isChangeTabTitleVisible={isChangeTabTitleVisible}
          setIsChangeTabTitleVisible={setIsChangeTabTitleVisible}
          selectedTabTitle={selectedTabTitle}
          setSelectedTabTitle={setSelectedTabTitle}
          dashboard={dashboard}
          setDashboard={setDashboard}
          tabId={tabId}
        />
      )}
      {isImportDashboardDialogVisible && (
        <ImportDashboardOrTab
          isImportDashboardDialogVisible={isImportDashboardDialogVisible}
          setIsImportDashboardDialogVisibility={setIsImportDashboardDialogVisibility}
          dashboard={dashboard}
        />
      )}
      {tabConfiguratorvisibility && (
        <DashboardTabConfigurator
          onHide={() => setTabConfiguratorVisibility(false)}
          currentContextType={currentTabContextType}
          tabId={tabId}
          dashboard={dashboard}
          setDashboard={setDashboard}
        />
      )}
      {addNewGroupDialogVisibility && (
        <AddNewTabGroup
          dashboard={dashboard}
          addNewGroupDialogVisibility={addNewGroupDialogVisibility}
          setAddNewGroupDialogVisibility={setAddNewGroupDialogVisibility}
          tabId={tabId}
          updateQuery={updateQuery}
        />
      )}
      {reorderTabsDialogVisibility && (
        <Reorder
          onChange={onTabChange}
          setReorderDialogVisibility={setReorderTabsDialogVisibility}
          reorderDialogVisibility={reorderTabsDialogVisibility}
          reorderType={ReorderType.TABS}
        />
      )}
      {isCreateDatasetDialogVisible && (
        <Dialog
          header={T("action.create-dataset")}
          visible={isCreateDatasetDialogVisible}
          onHide={() => setIsCreateDatasetDialogVisible(false)}
          style={{ width: "50vw" }}
          modal={false}
        >
          <CreateDatasetDialog setCreateDialogVisibility={setIsCreateDatasetDialogVisible} />
        </Dialog>
      )}
      {isDatasetSearchDialogVisible && (
        <DatasetSearchDialog
          dashboard={dashboard}
          isDatasetSearchDialogVisible={isDatasetSearchDialogVisible}
          setIsDatasetSearchDialogVisible={setIsDatasetSearchDialogVisible}
        />
      )}
      {isChangeTabsDisplayTypeDialogVisible && (
        <ChangeTabsDisplayTypeDialog
          isChangeTabsDisplayTypeDialogVisible={isChangeTabsDisplayTypeDialogVisible}
          setChangeTabsDisplayTypeDialogVisibility={setChangeTabsDisplayTypeDialogVisibility}
          dashboard={dashboard}
          setDashboard={setDashboard}
        />
      )}
    </>
  );
};

export default DashboardMain;
